import { Box, Tab, Tabs, TextField } from "@mui/material";
import { FC, useMemo, useState } from "react";
import { getServices, Service } from "src/apis/services";
import PageContainer from "src/pages/components/PageContainer";
import { getFirstDayOfMonth, getLastDayOfMonth } from "src/utils/date";
import { getUserId, isUserPsychologist } from "src/utils/auth";
import StartAndEndDateFilters from "src/pages/components/StartAndEndDateFilters";
import FilterButtons from "src/pages/components/SearchAndResetButtons";
import SkeletonLoad from "src/pages/components/SkeletonLoad";
import PsychologistServicesTable from "./components/PsychologistServicesTable";
import { getPsychologistServicesSummary, PsychologistServicesSummary } from "src/apis/psychologist";
import SummaryFab from "src/pages/components/SummaryFab";
import ServicesByPsychologistSummaryTable from "src/pages/Admin/ServicesByPsychologist/components/ServicesByPsychologistSummaryTable";
import ServicesCalendar from "./components/ServicesCalendar";

const MyServices: FC = (): JSX.Element => {
  const [filterStartDate, setFilterStartDate] = useState(getFirstDayOfMonth());
  const [filterEndDate, setFilterEndDate] = useState(getLastDayOfMonth());
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [services, setServices] = useState<Service[]>([]);
  const [summary, setSummary] = useState<PsychologistServicesSummary|null>(null);
  const [activeTab, setActiveTab] = useState<'list' | 'calendar'>('list');
  const psychologistId = useMemo(() => getUserId(), []);

  const filteredServices = useMemo(() => {
    const query = searchQuery
      .trim()
      .toLocaleLowerCase("pt-BR")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "");

    if (!query) {
      return services;
    }

    return services.filter((service) => {
      const collaboratorName = service.colaborador
        .toLocaleLowerCase("pt-BR")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "");
      const companyName = service.empresa
        .toLocaleLowerCase("pt-BR")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "");

      return (
        collaboratorName.includes(query) || companyName.includes(query)
      );
    });
  }, [searchQuery, services]);

  const fetchData = async (): Promise<void> => {
    if (!psychologistId) return;
    setIsLoading(true);

    try {
      const response = await getServices(
        filterStartDate,
        filterEndDate,
        psychologistId,
        searchQuery.trim() || undefined
      );

      setServices(response);

      if (response.length > 0) {
        const resSummary = await getPsychologistServicesSummary(
          filterStartDate,
          filterEndDate,
          psychologistId
        );
        setSummary(resSummary);
      }

      setError("");
    } catch (err: any) {
      setError(err?.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContainer title="Meus atendimentos" error={error} guard={isUserPsychologist}>
      <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)}>
        <Tab label="Lista" value="list" />
        <Tab label="Calendário" value="calendar" />
      </Tabs>

      {activeTab === 'list' && (
        <>
          {Boolean(summary) && (
            <SummaryFab marginBottom={7} >
              <ServicesByPsychologistSummaryTable
                summary={summary!}
                startDate={filterStartDate}
                endDate={filterEndDate}
              />
            </SummaryFab>
          )}

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "0.5rem",
              justifyContent: "center",
              alignItems: "center",
              width: "100%"
            }}
          >
            <StartAndEndDateFilters
              startDate={filterStartDate}
              setStartDate={setFilterStartDate}
              endDate={filterEndDate}
              setEndDate={setFilterEndDate}
            />
            <TextField
              label="Buscar colaborador ou empresa"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              sx={{ minWidth: 300 }}
            />
            <FilterButtons
              isLoading={isLoading}
              registerUrl="/atendimentos/cadastro"
              search={() => {
                fetchData();
              }}
              resetState={() => {
                setFilterStartDate(getFirstDayOfMonth());
                setFilterEndDate(getLastDayOfMonth());
                setSearchQuery("");
                setServices([]);
              }}
            />
          </Box>

          <Box>
            {isLoading && (
              <SkeletonLoad variant="table" rows={6} columns={10} />
            )}

            {!error && (
              <PsychologistServicesTable
                services={filteredServices}
                setError={setError}
                onStatusUpdated={fetchData}
              />
            )}
          </Box>
        </>
      )}

      {activeTab === 'calendar' && (
        <ServicesCalendar psychologistId={psychologistId} />
      )}
    </PageContainer>
  );
}

export default MyServices;
